import { useEffect, useImperativeHandle, useRef, useState, type Ref } from 'react'
import type { Message } from '../../api/types'
import { MAX_BODY_CHARS } from '../../lib/files'
import { editMessage } from './messageActions'

export interface EditFormHandle {
  /** Whether the text differs from the message's. */
  changed: () => boolean
}

interface Props {
  message: Message
  /** Goes up each time the form should take focus again (it takes focus as it opens). */
  focusRequests?: number
  onDone: () => void
  ref?: Ref<EditFormHandle>
}

export function EditForm({ message, focusRequests = 0, onDone, ref }: Props) {
  const [body, setBody] = useState(message.body)
  const [busy, setBusy] = useState(false)
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  /** Cancelled while a save was on its way: the save's result is no longer this form's to act on. */
  const open = useRef(true)

  useEffect(() => {
    open.current = true
    return () => {
      open.current = false
    }
  }, [])

  useImperativeHandle(ref, () => ({ changed: () => body !== message.body }), [body, message.body])

  useEffect(() => {
    if (focusRequests > 0) textareaRef.current?.focus()
  }, [focusRequests])

  const trimmed = body.trim()
  const invalid = (!trimmed && message.attachments.length === 0) || body.length > MAX_BODY_CHARS

  const save = async () => {
    if (invalid) return
    if (body === message.body) return onDone()
    setBusy(true)
    const ok = await editMessage(message.id, body)
    if (!open.current) return
    setBusy(false)
    if (ok) onDone()
  }

  return (
    <form
      className="edit-form"
      onSubmit={(e) => {
        e.preventDefault()
        void save()
      }}
      onKeyDown={(e) => {
        // Escape anywhere in the form cancels the edit (spec §3.7), but not while an input method is composing.
        if (e.key === 'Escape' && !e.nativeEvent.isComposing) {
          e.preventDefault()
          onDone()
        }
      }}
    >
      <textarea
        ref={textareaRef}
        aria-label="Edit message"
        value={body}
        autoFocus
        onChange={(e) => setBody(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
            e.preventDefault()
            void save()
          }
        }}
      />
      <div className="edit-actions">
        <span>Escape to cancel · Enter to save</span>
        <button type="button" className="button small" onClick={onDone}>
          Cancel
        </button>
        <button type="submit" className="button primary small" disabled={busy || invalid}>
          Save
        </button>
      </div>
    </form>
  )
}
